import type { GamificationData } from "@/types";
import type { StorageManager } from "@/utils/storage";
import { eventManager } from "@/utils/event-manager";

/**
 * XP data structure
 */
interface XPData {
  total: number;
  level: number;
}

/**
 * Streak data structure
 */
interface StreakData {
  current: number;
  daily: number;
  best: number;
  totalDays: number;
}

/**
 * XP reward result
 */
interface XPReward {
  added: number;
  total: number;
  levelUp: boolean;
  newLevel: number;
  reason: string;
}

/**
 * Gamification system (XP, levels, streaks)
 */
export class GamificationSystem {
  private storage: StorageManager;
  public xp: XPData;
  public streaks: StreakData;
  private lastPracticeDate: Date | null;

  constructor(storageManager: StorageManager) {
    this.storage = storageManager;
    this.xp = this.loadXP();
    this.streaks = this.loadStreaks();
    this.lastPracticeDate = this.loadLastPracticeDate();
  }

  /**
   * Load XP data
   */
  private loadXP(): XPData {
    return (
      this.storage.load("xp", {
        total: 0,
        level: 1,
      }) || {
        total: 0,
        level: 1,
      }
    );
  }

  /**
   * Load streak data
   */
  private loadStreaks(): StreakData {
    return (
      this.storage.load("streaks", {
        current: 0,
        daily: 0,
        best: 0,
        totalDays: 0,
      }) || {
        current: 0,
        daily: 0,
        best: 0,
        totalDays: 0,
      }
    );
  }

  /**
   * Load last practice date
   */
  private loadLastPracticeDate(): Date | null {
    const dateString = this.storage.load<string | null>("last-practice-date");
    return dateString ? new Date(dateString) : null;
  }

  /**
   * Add XP for correct answer
   */
  addXP(amount: number, reason: string = "correct answer"): XPReward {
    const oldLevel = this.getLevel();
    this.xp.total += amount;
    const newLevel = this.getLevel();

    this.saveXP();

    // Check for level up
    if (newLevel > oldLevel) {
      eventManager.emit("gamification-updated", this.getGamificationData());
    }

    return {
      added: amount,
      total: this.xp.total,
      levelUp: newLevel > oldLevel,
      newLevel: newLevel,
      reason,
    };
  }

  /**
   * Update streaks
   */
  updateStreaks(isCorrect: boolean): StreakData {
    const today = new Date();
    const todayString = today.toDateString();
    const lastPracticeDateString = this.lastPracticeDate
      ? this.lastPracticeDate.toDateString()
      : null;

    // Update current streak
    if (isCorrect) {
      this.streaks.current++;
      if (this.streaks.current > this.streaks.best) {
        this.streaks.best = this.streaks.current;
      }
    } else {
      this.streaks.current = 0;
    }

    // Update daily streak
    if (!lastPracticeDateString || lastPracticeDateString !== todayString) {
      // First practice of the day
      if (this.lastPracticeDate) {
        const daysDiff = Math.floor(
          (today.getTime() - this.lastPracticeDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );

        if (daysDiff === 1) {
          // Consecutive day
          this.streaks.daily++;
          this.streaks.totalDays++;
        } else if (daysDiff > 1) {
          // Broke the streak
          this.streaks.daily = 1;
          this.streaks.totalDays++;
        }
        // Same day = no change to daily streak
      } else {
        // First day ever
        this.streaks.daily = 1;
        this.streaks.totalDays = 1;
      }

      this.lastPracticeDate = today;
      this.saveLastPracticeDate();
    }

    this.saveStreaks();

    eventManager.emit("gamification-updated", this.getGamificationData());

    return this.streaks;
  }

  /**
   * Calculate current level based on XP
   */
  getLevel(): number {
    // Level progression: 100, 250, 450, 700, 1000, 1350, 1750, 2200, 2700, 3250, ...
    // Formula: level n requires sum of (50 + 50*i) for i=1 to n
    let level = 1;
    let requiredXP = 0;

    while (requiredXP <= this.xp.total) {
      level++;
      requiredXP += 50 * level;
    }

    return level - 1;
  }

  /**
   * Get XP for next level
   */
  getXPForNextLevel(): number {
    const currentLevel = this.getLevel();
    return 50 * (currentLevel + 1);
  }

  /**
   * Get XP progress for current level
   */
  getXPProgress(): number {
    const currentLevel = this.getLevel();
    const xpForCurrentLevel =
      currentLevel === 1
        ? 0
        : Array.from(
            { length: currentLevel - 1 },
            (_, i) => 50 * (i + 2)
          ).reduce((sum, xp) => sum + xp, 0);

    const xpInCurrentLevel = this.xp.total - xpForCurrentLevel;
    const xpNeededForNextLevel = this.getXPForNextLevel();

    return Math.min(
      100,
      Math.round((xpInCurrentLevel / xpNeededForNextLevel) * 100)
    );
  }

  /**
   * Get gamification data for UI
   */
  getGamificationData(): GamificationData {
    const level = this.getLevel();
    const xpProgress = this.getXPProgress();
    const pointsToNextLevel = this.getXPForNextLevel();

    return {
      level,
      xp: {
        total: this.xp.total,
        pointsToNextLevel,
      },
      xpProgress,
      streaks: {
        current: this.streaks.current,
        daily: this.streaks.daily,
        longest: this.streaks.best,
      },
    };
  }

  /**
   * Award daily login bonus
   */
  awardDailyBonus(): XPReward {
    const bonusXP = 25; // Daily bonus XP amount
    return this.addXP(bonusXP, "daily login bonus");
  }

  /**
   * Get total XP required for a specific level
   */
  getXPForLevel(level: number): number {
    if (level <= 1) return 0;
    return Array.from({ length: level - 1 }, (_, i) => 50 * (i + 2)).reduce(
      (sum, xp) => sum + xp,
      0
    );
  }

  /**
   * Reset gamification data
   */
  reset(options: { resetXP?: boolean; resetStreaks?: boolean } = {}): void {
    if (options.resetXP) {
      this.xp = { total: 0, level: 1 };
      this.saveXP();
    }

    if (options.resetStreaks) {
      this.streaks = { current: 0, daily: 0, best: 0, totalDays: 0 };
      this.lastPracticeDate = null;
      this.saveStreaks();
      this.saveLastPracticeDate();
    }
  }

  /**
   * Save methods
   */
  private saveXP(): void {
    this.storage.save("xp", this.xp);
  }

  private saveStreaks(): void {
    this.storage.save("streaks", this.streaks);
  }

  private saveLastPracticeDate(): void {
    const dateString = this.lastPracticeDate
      ? this.lastPracticeDate.toISOString()
      : null;
    this.storage.save("last-practice-date", dateString);
  }
}

// Export for global usage
export default GamificationSystem;
